<template>
  <div class="feedback-page">
    <h1>{{ t('feedback.title') }}</h1>

    <form @submit.prevent="submitFeedbackForm" class="feedback-form">
      <div class="form-group">
        <label for="feedback-category">{{ t('feedback.form.category.label') }}</label>
        <select id="feedback-category" v-model="feedback.category">
          <option
            v-for="option in FEEDBACK_CATEGORIES"
            :key="option.value"
            :value="option.value"
          >
            {{ t(option.labelKey) }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label for="feedback-description">{{ t('feedback.form.description.label') }}</label>
        <textarea
          id="feedback-description"
          v-model="feedback.description"
          required
          maxlength="500"
          :placeholder="t('feedback.form.description.placeholder')"
        ></textarea>
        <div class="char-count">{{ feedback.description.length }}/500</div>
        <div v-if="errors.description" class="error-text">{{ errors.description }}</div>
      </div>

      <div class="form-group">
        <label for="feedback-attachment">{{ t('feedback.form.attachment.label') }}</label>
        <input
          type="file"
          id="feedback-attachment"
          ref="fileInputRef"
          @change="handleFileChange"
          accept=".png,.jpg,.jpeg,.gif,.txt,.pdf,.log,.zip,.rar"
        >
        <div v-if="feedback.attachmentName" class="file-name-display">
          {{ t('feedback.form.attachment.selected', { name: feedback.attachmentName }) }}
          <button type="button" @click="clearAttachment" class="clear-file-btn">&times;</button>
        </div>
        <div v-if="errors.attachment" class="error-text">{{ errors.attachment }}</div>
      </div>

      <button type="submit" class="submit-btn" :disabled="isSubmitting">
        {{ isSubmitting ? t('feedback.submit.inProgress') : t('feedback.submit.cta') }}
      </button>
    </form>

    <div v-if="successMessage" class="success-message">
      {{ successMessage }}
    </div>
    <div v-if="submitError" class="error-message">
      {{ submitError }}
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { submitFeedback } from '@/services/api';

const FEEDBACK_CATEGORIES = [
  { value: 'feature', backendValue: '\u529f\u80fd\u5efa\u8bae', labelKey: 'feedback.form.category.options.feature' },
  { value: 'issue', backendValue: '\u95ee\u9898\u53cd\u9988', labelKey: 'feedback.form.category.options.issue' },
  { value: 'other', backendValue: '\u5176\u4ed6', labelKey: 'feedback.form.category.options.other' }
];

// 声明响应式变量
const feedback = ref({
  category: FEEDBACK_CATEGORIES[0].value,
  description: '',
  attachment: null,
  attachmentName: ''
});
const isSubmitting = ref(false); // 保留这一个声明
const successMessage = ref('');
const errors = ref({});
const submitError = ref(''); // 检查是否有其他类似功能的变量
const fileInputRef = ref(null);
const { t } = useI18n();
// 删除此处的重复声明 const isSubmitting = ref(false);

const handleFileChange = (event) => { // Renamed from handleFileUpload
  const file = event.target.files[0];
  errors.value.attachment = '';    // Clear previous attachment error
  feedback.value.attachment = null; // Reset file object in feedback ref
  feedback.value.attachmentName = ''; // Reset file name display in feedback ref

  if (file) {
    if (file.size > 2 * 1024 * 1024) { // 2MB limit
      errors.value.attachment = t('feedback.form.attachment.errors.size');
      clearFileInput(); // Reset the input field
      return;
    }
    // More permissive validation to align with typical backend expectations
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.txt', '.pdf', '.log', '.zip', '.rar'];
    const fileName = file.name.toLowerCase();
    const fileExtensionIsValid = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!fileExtensionIsValid) {
      errors.value.attachment = t('feedback.form.attachment.errors.type');
      clearFileInput();
      return;
    }
    feedback.value.attachment = file; // Store the File object
    feedback.value.attachmentName = file.name; // For display
  }
};

const clearAttachment = () => {
    feedback.value.attachment = null; // Clear the File object
    feedback.value.attachmentName = ''; // Clear the display name
    clearFileInput(); // Visually clear the file input
};

const clearFileInput = () => {
    if (fileInputRef.value) {
        fileInputRef.value.value = '';
    }
};

const validateForm = () => {
  errors.value = {};
  let isValid = true;

  if (!feedback.value.description.trim()) {
    errors.value.description = t('feedback.form.description.errors.required');
    isValid = false;
  } else if (feedback.value.description.trim().length < 10) { // Added min length check
    errors.value.description = t('feedback.form.description.errors.minLength');
    isValid = false;
  } else if (feedback.value.description.length > 500) { // Max length check should use raw length
    errors.value.description = t('feedback.form.description.errors.maxLength');
    isValid = false;
  }

  if (errors.value.attachment) {
      isValid = false;
  }

  return isValid;
};

const submitFeedbackForm = async () => {
  successMessage.value = '';
  submitError.value = '';

  if (!validateForm()) {
    return;
  }

  isSubmitting.value = true;

  const feedbackPayload = {
    category:
      FEEDBACK_CATEGORIES.find(item => item.value === feedback.value.category)?.backendValue ||
      feedback.value.category,
    description: feedback.value.description,
    attachmentFile: feedback.value.attachment // Pass the File object stored in feedback.value.attachment
  };

  try {
    const response = await submitFeedback(feedbackPayload);

    successMessage.value = t('feedback.submit.success');
    if (response && response.id) {
        console.log("Feedback submitted successfully, ID:", response.id);
    }

    // 重置表单字段
    feedback.value.category = FEEDBACK_CATEGORIES[0].value;
    feedback.value.description = '';
    feedback.value.attachment = null;
    feedback.value.attachmentName = '';
    isSubmitting.value = false;
    errors.value = {}; // 清除验证错误

    setTimeout(() => {
      successMessage.value = '';
    }, 5000);
  } catch (error) {
    console.error('Failed to submit feedback:', error);
    const reason = error?.message || t('common.unknownError');
    submitError.value = t('feedback.submit.error', { reason });
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.feedback-page {
  max-width: 700px;
  margin: 20px auto;
  padding: 25px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 15px rgba(0,0,0,0.08);
  font-family: Arial, sans-serif;
}

h1 {
  text-align: center;
  color: #333;
  margin-bottom: 25px;
}

.feedback-form .form-group {
  margin-bottom: 20px;
}

.feedback-form label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #555;
  font-size: 0.95em;
}

.feedback-form select,
.feedback-form textarea,
.feedback-form input[type="file"] {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 1em;
}

.feedback-form textarea {
  min-height: 120px;
  resize: vertical;
}

.char-count {
  text-align: right;
  font-size: 0.8em;
  color: #777;
  margin-top: 4px;
}

.file-name-display {
  font-size: 0.9em;
  color: #555;
  margin-top: 8px;
  padding: 8px;
  background-color: #f0f0f0;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.clear-file-btn {
  background: none;
  border: none;
  color: #900;
  font-size: 1.2em;
  cursor: pointer;
  padding: 0 5px;
}


.feedback-form .error-text {
  color: #d9534f; /* Bootstrap's text-danger color */
  font-size: 0.85em;
  margin-top: 5px;
}

.submit-btn {
  display: block;
  width: 100%;
  padding: 12px;
  background-color: #5cb85c; /* Bootstrap's btn-success color */
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.1em;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.submit-btn:hover {
  background-color: #4cae4c;
}

.submit-btn:disabled {
  background-color: #aaa;
  cursor: not-allowed;
}

.success-message {
  margin-top: 20px;
  padding: 15px;
  background-color: #dff0d8; /* Bootstrap's alert-success color */
  color: #3c763d;
  border: 1px solid #d6e9c6;
  border-radius: 4px;
  text-align: center;
}

.error-message {
  margin-top: 20px;
  padding: 15px;
  background-color: #f2dede; /* Bootstrap's alert-danger color */
  color: #a94442;
  border: 1px solid #ebccd1;
  border-radius: 4px;
  text-align: center;
}
</style>
